import type { DraftPayload, OptionsFromRows } from '@datahub/shared'
import { rowOptionValues } from '@datahub/shared'

export const NOT_IN_ROWS_SUFFIX = '(not in your fund vehicles)'
/** The value of the leading "None" option of a clearable select. It is never stored. */
export const CLEAR_VALUE = '__none__'

/** The hint under a row-backed control that has no rows to choose from. `tabLabel` is the tab where the rows are entered. */
export function emptyRowsHint(tabLabel: string): string {
  return `Add your fund vehicles in the ${tabLabel} tab first.`
}

/** What a select stores for a choice: nothing at all for the "None" option, so a cleared field reads as untouched. */
export function clearableValue(choice: string): string | undefined {
  return choice === CLEAR_VALUE ? undefined : choice
}

export interface RowSelectOptions {
  options: { value: string; label: string }[]
  /** True when the draft has no rows to choose from. */
  empty: boolean
}

function storedNames(value: unknown): string[] {
  const list = Array.isArray(value) ? value : [value]
  return list.filter((v): v is string => typeof v === 'string').map((v) => v.trim()).filter((v) => v !== '')
}

/**
 * The value to give a row-backed single select. The options are trimmed row names, so the
 * stored value is trimmed too. Then " Alpha " shows as the option "Alpha", selected.
 */
export function rowSelectValue(stored: unknown): string {
  return typeof stored === 'string' ? stored.trim() : ''
}

/**
 * The values to give a row-backed multiselect: the trimmed stored names, each one time.
 * The control changes this list, so a change stores trimmed names.
 */
export function rowMultiValue(stored: unknown): string[] {
  return [...new Set(storedNames(stored))]
}

/**
 * The options for a select whose choices are the values of one field on the draft's rows.
 * A stored value that is not among the rows stays in the list, marked, so that an older
 * answer is never hidden and never blocks a submission.
 */
export function rowSelectOptions(
  source: OptionsFromRows,
  payload: DraftPayload | undefined,
  stored: unknown,
  opts: { clearable?: boolean } = {},
): RowSelectOptions {
  const names = rowOptionValues(payload, source)
  const options = names.map((name) => ({ value: name, label: name }))
  const listed = new Set(names)
  for (const name of storedNames(stored)) {
    if (listed.has(name)) continue
    listed.add(name)
    options.push({ value: name, label: `${name} ${NOT_IN_ROWS_SUFFIX}` })
  }
  if (opts.clearable && options.length > 0) options.unshift({ value: CLEAR_VALUE, label: 'None' })
  return { options, empty: names.length === 0 }
}

/**
 * The accessible name of a currency select. The control shows the ISO code and nothing
 * else, so the name holds that code (WCAG 2.5.3). The money input has no visible label
 * of its own for the select, so its name also says "currency".
 */
export function currencyAriaLabel(fieldLabel: string, code: string | undefined, kind: 'money' | 'select'): string {
  const base = kind === 'money' ? `${fieldLabel} currency` : fieldLabel
  return code ? `${base}, ${code}` : base
}
